import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ChevronLeft, ChevronRight, Award, ArrowLeft,
  Loader2, AlertTriangle, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { generateCertificatePDF } from '../components/course-viewer/generate-certificate';
import { CourseCompletionScreen } from '../components/course-viewer/CourseCompletionScreen';
import { CourseSidebar, CourseMobileNav } from '../components/course-viewer/CourseSidebar';
import { CourseSlideContent } from '../components/course-viewer/CourseSlideContent';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CourseViewerPage = () => {
  const { catalogId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const audioRef = useRef(null);

  const [course, setCourse] = useState(null);
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [imageUrl, setImageUrl] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);

  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResults, setQuizResults] = useState(null);
  const [quizSubmitting, setQuizSubmitting] = useState(false);

  const [exerciseRevealed, setExerciseRevealed] = useState(false);
  const [exerciseAnswer, setExerciseAnswer] = useState('');

  const [completionResult, setCompletionResult] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [certDownloading, setCertDownloading] = useState(false);

  const [evalStep, setEvalStep] = useState('none');
  const [evalRatings, setEvalRatings] = useState({ satisfaction: 0, content_quality: 0, clarity: 0, applicability: 0 });
  const [evalComment, setEvalComment] = useState('');
  const [evalSubmitting, setEvalSubmitting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const quizSlide = slides.find(s => s.type === 'quiz');
  const quizSlideId = quizSlide?.id;
  const quizScoreFromProgress = progress?.quiz_scores?.[quizSlideId];
  const hasSubmittedQuiz = !!quizResults || !!quizScoreFromProgress;

  const fetchCourse = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/courses/${catalogId}`, { headers });
      setCourse(res.data);
      setSlides(res.data.slides || []);
      setProgress(res.data.progress);
      if (res.data.progress?.completed_slides?.length) {
        const lastCompleted = res.data.progress.completed_slides.length;
        setCurrentSlide(Math.min(lastCompleted, (res.data.slides || []).length - 1));
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [catalogId, token]);

  useEffect(() => {
    if (token && catalogId) fetchCourse();
  }, [token, catalogId, fetchCourse]);

  useEffect(() => {
    if (!slides[currentSlide]) return;
    const slide = slides[currentSlide];
    setAudioUrl(null);
    setImageUrl(null);
    setAudioPlaying(false);
    if (audioRef.current) audioRef.current.pause();

    if (slide.has_image) {
      const imgUrl = `${API}/courses/${catalogId}/slides/${slide.id}/image`;
      fetch(imgUrl, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.blob())
        .then(blob => setImageUrl(URL.createObjectURL(blob)))
        .catch(() => {});
    }

    if (slide.has_audio) {
      setAudioLoading(true);
      const url = `${API}/courses/${catalogId}/slides/${slide.id}/audio`;
      fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.blob())
        .then(blob => { setAudioUrl(URL.createObjectURL(blob)); setAudioLoading(false); })
        .catch(() => setAudioLoading(false));
    }
    setQuizResults(null);
    setQuizAnswers({});
    setExerciseRevealed(false);
    setExerciseAnswer('');
  }, [currentSlide, slides]);

  const toggleAudio = () => {
    if (!audioRef.current || !audioUrl) return;
    if (audioPlaying) { audioRef.current.pause(); setAudioPlaying(false); }
    else { audioRef.current.play(); setAudioPlaying(true); }
  };

  const toggleMute = () => {
    setAudioMuted(!audioMuted);
    if (audioRef.current) audioRef.current.muted = !audioMuted;
  };

  const markSlideCompleted = async (slideId) => {
    try {
      const res = await axios.post(`${API}/courses/${catalogId}/progress`, { slide_id: slideId, completed: true }, { headers });
      setProgress(res.data);
    } catch {}
  };

  const goToSlide = (index) => {
    if (index < 0 || index >= slides.length) return;
    const currentSlideData = slides[currentSlide];
    if (currentSlideData && currentSlideData.type === 'lesson') markSlideCompleted(currentSlideData.id);
    setCurrentSlide(index);
  };

  const handleQuizSubmit = async () => {
    const slide = slides[currentSlide];
    if (!slide || slide.type !== 'quiz') return;
    const questions = slide.quiz_data?.questions || [];
    const answers = questions.map((_, i) => quizAnswers[i] ?? -1);
    setQuizSubmitting(true);
    try {
      const res = await axios.post(`${API}/courses/${catalogId}/quiz-submit`, { slide_id: slide.id, answers }, { headers });
      setQuizResults(res.data);
      markSlideCompleted(slide.id);
      if (res.data.score >= 70) toast.success(`Quiz reussi ! Score: ${res.data.score}%`);
      else toast.error(`Score insuffisant: ${res.data.score}%. Il faut 70% minimum.`);
    } catch { toast.error("Erreur lors de la soumission du quiz"); }
    finally { setQuizSubmitting(false); }
  };

  const handleCompleteCourse = async () => {
    setCompleting(true);
    try {
      const res = await axios.post(`${API}/courses/${catalogId}/complete`, {}, { headers });
      setCompletionResult(res.data);
      if (res.data.passed) {
        try {
          const evalRes = await axios.get(`${API}/courses/${catalogId}/evaluation`, { headers });
          setEvalStep(evalRes.data.has_evaluation ? 'done' : 'form');
        } catch { setEvalStep('form'); }
        toast.success("Formation validee !");
      } else {
        toast.error(`Score insuffisant: ${res.data.overall_score}%`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setCompleting(false); }
  };

  const handleSubmitEvaluation = async () => {
    const { satisfaction, content_quality, clarity, applicability } = evalRatings;
    if (!satisfaction || !content_quality || !clarity || !applicability) { toast.error("Veuillez noter tous les criteres"); return; }
    setEvalSubmitting(true);
    try {
      await axios.post(`${API}/courses/${catalogId}/evaluation`, { satisfaction, content_quality, clarity, applicability, comment: evalComment }, { headers });
      setEvalStep('done');
      toast.success("Merci pour votre évaluation !");
    } catch (err) {
      if (err.response?.status === 409) setEvalStep('done');
      else toast.error(err.response?.data?.detail || "Erreur");
    } finally { setEvalSubmitting(false); }
  };

  const handleRestart = async () => {
    try {
      await axios.post(`${API}/courses/${catalogId}/restart`, {}, { headers });
      setCurrentSlide(0); setCompletionResult(null); setProgress(null);
      fetchCourse(); toast.success("Formation reinitialisee");
    } catch { toast.error("Erreur lors de la reinitialisation"); }
  };

  const handleDownloadCert = async () => {
    setCertDownloading(true);
    try {
      const res = await axios.get(`${API}/courses/${catalogId}/certificate`, { headers });
      generateCertificatePDF(res.data);
      toast.success("Attestation telechargee !");
    } catch { toast.error("Erreur lors du telechargement de l'attestation"); }
    finally { setCertDownloading(false); }
  };

  const handleRevealExercise = () => {
    setExerciseRevealed(true);
    markSlideCompleted(slides[currentSlide].id);
  };

  /* Loading / Error states */
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 pt-24" data-testid="course-loading">
      <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 pt-24" data-testid="course-error">
      <div className="text-center max-w-md">
        <Lock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Acces refuse</h2>
        <p className="text-slate-500 mb-6">{error}</p>
        <Button onClick={() => navigate('/espace-client')} className="bg-teal-600 hover:bg-teal-700 text-white">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'espace client
        </Button>
      </div>
    </div>
  );

  if (!course || slides.length === 0) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 pt-24">
      <p className="text-slate-500">Aucun contenu disponible pour cette formation.</p>
    </div>
  );

  const currentSlideData = slides[currentSlide];
  const completedCount = progress?.completed_slides?.length || 0;
  const progressPercent = Math.round((completedCount / slides.length) * 100);
  const isLastSlide = currentSlide === slides.length - 1;

  return (
    <div className="min-h-screen bg-slate-50 pt-20" data-testid="course-viewer">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/espace-client')} className="flex items-center gap-2 text-slate-600 hover:text-teal-600 text-sm font-medium" data-testid="btn-back">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="flex-1 mx-6">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold text-slate-900 truncate">{course.title}</span>
              <span className="text-xs text-slate-500 ml-2 shrink-0">{completedCount}/{slides.length}</span>
            </div>
            <Progress value={progressPercent} className="h-2" data-testid="progress-bar" />
          </div>
          <span className="text-sm font-bold text-teal-600">{progressPercent}%</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {completionResult ? (
          <CourseCompletionScreen
            completionResult={completionResult}
            evalStep={evalStep}
            evalRatings={evalRatings}
            evalComment={evalComment}
            evalSubmitting={evalSubmitting}
            certDownloading={certDownloading}
            onSetEvalRatings={setEvalRatings}
            onSetEvalComment={setEvalComment}
            onSubmitEvaluation={handleSubmitEvaluation}
            onRestart={handleRestart}
            onDownloadCert={handleDownloadCert}
            onNavigateBack={() => navigate('/espace-client')}
          />
        ) : (
          <div className="grid lg:grid-cols-[240px_1fr] gap-6">
            <CourseSidebar slides={slides} currentSlide={currentSlide} progress={progress} onGoToSlide={goToSlide} />
            <div className="space-y-4">
              <CourseSlideContent
                currentSlide={currentSlide}
                slides={slides}
                imageUrl={imageUrl}
                audioUrl={audioUrl}
                audioLoading={audioLoading}
                audioPlaying={audioPlaying}
                audioMuted={audioMuted}
                audioRef={audioRef}
                quizAnswers={quizAnswers}
                quizResults={quizResults}
                quizSubmitting={quizSubmitting}
                quizScoreFromProgress={quizScoreFromProgress}
                quizSlideId={quizSlideId}
                exerciseAnswer={exerciseAnswer}
                exerciseRevealed={exerciseRevealed}
                onToggleAudio={toggleAudio}
                onToggleMute={toggleMute}
                onAudioEnded={() => setAudioPlaying(false)}
                onSetQuizAnswers={setQuizAnswers}
                onSubmitQuiz={handleQuizSubmit}
                onSetExerciseAnswer={setExerciseAnswer}
                onRevealExercise={handleRevealExercise}
              />

              {/* Navigation */}
              <div className="flex items-center justify-between" data-testid="slide-navigation">
                <Button variant="outline" onClick={() => goToSlide(currentSlide - 1)} disabled={currentSlide === 0} data-testid="btn-prev-slide">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Precedent
                </Button>
                {isLastSlide ? (
                  <div className="flex flex-col items-end gap-1">
                    {!hasSubmittedQuiz && (
                      <p className="text-xs text-amber-600 flex items-center gap-1" data-testid="quiz-warning">
                        <AlertTriangle className="w-3.5 h-3.5" /> Completez le quiz avant de terminer
                      </p>
                    )}
                    <Button
                      onClick={() => { markSlideCompleted(currentSlideData.id); handleCompleteCourse(); }}
                      disabled={completing || !hasSubmittedQuiz}
                      className={`${hasSubmittedQuiz ? 'bg-teal-600 hover:bg-teal-700' : 'bg-slate-400 cursor-not-allowed'} text-white`}
                      data-testid="btn-complete-course"
                    >
                      {completing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Award className="w-4 h-4 mr-2" />}
                      Terminer la formation
                    </Button>
                  </div>
                ) : (
                  <Button onClick={() => goToSlide(currentSlide + 1)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="btn-next-slide">
                    Suivant <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>

              <CourseMobileNav slides={slides} currentSlide={currentSlide} progress={progress} onGoToSlide={goToSlide} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseViewerPage;
